import { useState, useEffect, useCallback } from 'react'
import { Loader2, Link2, Clipboard, Check, MousePointerClick, CalendarDays, ExternalLink } from 'lucide-react'
import { useT } from '../../hooks/useTranslation'
import { useHaptic } from '../../hooks/useHaptic'
import { getUtmStats, getUtmDynamics } from '../../services/api'
import { compactUtm, BOT_LINK_BASE, fetchBotUsername, buildBotLinkBase, buildStartLink } from '../../services/utm'

// Открытие ссылки: ВНУТРИ Telegram Mini App t.me-ссылки нельзя открыть обычным
// переходом/новой вкладкой — только Telegram.WebApp.openTelegramLink (для t.me)
// и openLink (для обычных http). Вне Telegram — window.open. Иначе «то
// открывается, то нет» (жалоба task.txt: ссылки в UTM-разделе не открываются).
function openUrl(url) {
  const tg = window.Telegram?.WebApp
  if (!tg) { window.open(url, '_blank', 'noopener'); return }
  try {
    if (url.startsWith('https://t.me/') && typeof tg.openTelegramLink === 'function') {
      tg.openTelegramLink(url)
      return
    }
    if (typeof tg.openLink === 'function') { tg.openLink(url); return }
  } catch { /* падаем в window.open ниже */ }
  window.open(url, '_blank', 'noopener')
}

// Веб-ссылка: адрес приложения. UTM в ней остаётся полным (utm_*=…) — веб-
// лендинг читает его из адресной строки и переносит в кнопку «Открыть в Telegram».
const WEB_LINK_BASE = 'https://scalpex.live'

// Подсказки для полей генератора (datalist — можно выбрать или ввести своё).
const SOURCE_OPTS = ['telegram', 'google', 'facebook', 'instagram', 'tiktok', 'youtube', 'partner', 'website', 'email']
const MEDIUM_OPTS = ['cpc', 'banner', 'referral', 'organic', 'social', 'email']
const CAMPAIGN_OPTS = ['launch', 'friend', 'promo', 'partner']
const TERM_OPTS = ['keywords', 'ads', 'influencer', 'review', 'push']
const CONTENT_OPTS = ['banner', 'post', 'video', 'reel', 'story', 'link', 'ad']

// Быстрые пресеты: один тап заполняет все поля под конкретный канал рекламы.
const PRESETS = (t) => [
  { key: 'tg',      label: t.utmPresetTg,      source: 'telegram',  medium: 'social',   campaign: 'channel', term: '',      content: '' },
  { key: 'google',  label: t.utmPresetGoogle,  source: 'google',    medium: 'cpc',      campaign: 'launch',  term: 'keywords', content: 'ad' },
  { key: 'fb',      label: t.utmPresetFb,      source: 'facebook',  medium: 'cpc',      campaign: 'promo',   term: '',      content: 'banner' },
  { key: 'ig',      label: t.utmPresetIg,      source: 'instagram', medium: 'social',   campaign: 'promo',   term: '',      content: 'reel' },
  { key: 'tt',      label: t.utmPresetTt,      source: 'tiktok',    medium: 'social',   campaign: 'promo',   term: '',      content: 'video' },
  { key: 'partner', label: t.utmPresetPartner, source: 'partner',   medium: 'referral', campaign: 'partner', term: '',      content: '' },
]

const DAYS_OPTS = [7, 14, 30]

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px',
  background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 10, color: '#fff', fontSize: 13.5, outline: 'none',
}
const labelStyle = { fontSize: 11, color: '#636366', marginBottom: 5, display: 'block' }

// Копирование в буфер с фолбэком через textarea (Telegram WebView иногда
// не даёт navigator.clipboard — document.execCommand('copy') работает).
function copyText(text) {
  return new Promise(resolve => {
    const fallback = () => {
      try {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(ta)
        resolve(ok)
      } catch { resolve(false) }
    }
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(() => resolve(true), fallback)
    else fallback()
  })
}

function Card({ title, icon, accent = '#34D399', children }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, padding: '14px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 10 }}>
        <span style={{ display: 'flex', color: accent }}>{icon}</span>
        <span>{title}</span>
      </div>
      {children}
    </div>
  )
}

function Row({ label, value, valueColor = '#fff', valueSub, subColor = '#636366' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 2px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <span style={{ fontSize: 13, color: '#8E8E93', lineHeight: 1.35, flex: 1, wordBreak: 'break-word' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 700, color: valueColor, textAlign: 'right', flexShrink: 0 }}>
        {value}
        {valueSub && <span style={{ fontSize: 11, color: subColor, fontWeight: 600, marginLeft: 5 }}>{valueSub}</span>}
      </span>
    </div>
  )
}

// Готовая ссылка: значение (выделяемое) + кнопки «Открыть» и «Копировать».
// «Открыть» — реальный механизм (см. openUrl выше): внутри Telegram Mini App
// обычная ссылка не открывается, нужен WebApp.openTelegramLink/openLink.
// t передаётся пропсом: раньше компонент брал t из области видимости UtmSection
// (он объявлен на уровне модуля) — при рендере падал ReferenceError и весь
// раздел UTM не открывался.
function LinkRow({ label, value, copied, onCopy, onOpen, t }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          readOnly
          value={value}
          onFocus={e => e.target.select()}
          style={{ ...inputStyle, flex: 1, minWidth: 0, fontFamily: 'monospace', fontSize: 11.5, color: '#B0B0B5' }}
        />
        <button
          onClick={onOpen}
          title={value}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
            background: 'rgba(129,140,248,0.12)',
            border: '1px solid rgba(129,140,248,0.4)',
            color: '#A5B4FC', fontSize: 12.5, fontWeight: 600,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <ExternalLink size={14} />
          {t.utmOpen}
        </button>
        <button
          onClick={onCopy}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            flexShrink: 0, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
            background: copied ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${copied ? 'rgba(16,185,129,0.35)' : 'rgba(255,255,255,0.1)'}`,
            color: copied ? '#34D399' : '#D0D0D5', fontSize: 12.5, fontWeight: 600,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {copied ? <Check size={14} /> : <Clipboard size={14} />}
          {copied ? t.utmCopied : t.utmCopy}
        </button>
      </div>
    </div>
  )
}

export default function UtmSection() {
  const t = useT()
  const haptic = useHaptic()

  // ── Генератор ссылок ─────────────────────────────────────────────────────
  const [source, setSource] = useState('telegram')
  const [medium, setMedium] = useState('partner')
  const [campaign, setCampaign] = useState('friend')
  const [term, setTerm] = useState('')
  const [content, setContent] = useState('')
  const [copied, setCopied] = useState('') // 'tg' | 'web' | ''

  // Реальное имя бота с бэкенда: ссылки строятся под него, а не под зашитую
  // константу (если бота переименуют — не будет «бот не найден»). До ответа
  // бэкенда (или при его недоступности) работаем на фолбэке BOT_LINK_BASE.
  const [botUsername, setBotUsername] = useState(null)
  useEffect(() => {
    let alive = true
    fetchBotUsername().then(u => { if (alive) setBotUsername(u || null) })
    return () => { alive = false }
  }, [])

  const enc = v => encodeURIComponent((v || '').trim())
  const hasParams = source.trim() || medium.trim() || campaign.trim() || term.trim() || content.trim()
  const utmQuery = [
    `utm_source=${enc(source)}`, `utm_medium=${enc(medium)}`, `utm_campaign=${enc(campaign)}`,
    term.trim() ? `utm_term=${enc(term)}` : '',
    content.trim() ? `utm_content=${enc(content)}` : '',
  ].filter(Boolean).join('&')
  // п.5 task.txt: стартовый параметр в Telegram ограничен 64 символами — полная
  // UTM-строка (75+) туда не влезает, и метки не доезжают. Кодируем КОРОТКО
  // (s/m/c/t/ct) через compactUtm; разбор умеет обе формы — и бот, и фронт.
  const tgStartapp = compactUtm({ source, medium, campaign, term, content })
  // База — из реального имени бота (или фолбэк-константа, пока бэкенд не ответил).
  const linkBase = buildBotLinkBase(botUsername)
  // Классический ?start= (работает у любого бота) — метку принимает бот и
  // записывает её сам. Форма ?startapp= требует главного мини-приложения в
  // BotFather и у нас не открывалась вовсе (2026-09-11, см. services/utm.js).
  const tgLink = buildStartLink(botUsername, hasParams ? tgStartapp : '')
  const webLink = hasParams ? `${WEB_LINK_BASE}/?${utmQuery}` : `${WEB_LINK_BASE}/`
  // Имя для подписи: реальное (с бэкенда) или из фолбэк-константы.
  const shownName = botUsername || BOT_LINK_BASE.replace('https://t.me/', '')

  const applyPreset = (p) => {
    setSource(p.source); setMedium(p.medium); setCampaign(p.campaign)
    setTerm(p.term); setContent(p.content); haptic.light()
  }

  const onCopy = async (which, text) => {
    await copyText(text)
    haptic.light()
    setCopied(which)
    setTimeout(() => setCopied(''), 1800)
  }

  // ── Статистика ───────────────────────────────────────────────────────────
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [days, setDays] = useState(14)
  const [dyn, setDyn] = useState(null)

  // Период применим и к сводке, и к динамике: один селектор на оба.
  const refresh = useCallback(() => {
    setLoading(true)
    getUtmStats(days)
      .then(s => setStats(s))
      .catch(e => setError(e?.message || t.utmError))
      .finally(() => setLoading(false))
    getUtmDynamics(days).then(d => setDyn(d)).catch(() => setDyn(null))
  }, [t.utmError, days])

  useEffect(() => { refresh() }, [refresh])

  const conv = (r) => (r.users > 0 ? Math.round((r.subscribers / r.users) * 100) + '%' : '—')
  const usd = (n) => '$' + (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })

  // Дни приходят новыми вперёд — для графика нужен хронологический порядок.
  const dynRows = (dyn?.rows || []).slice().reverse()
  const dynMax = Math.max(1, ...dynRows.map(r => r.users || 0))

  return (
    <div style={{ padding: '0 0 8px' }}>
      {/* ── Генератор UTM-ссылок ─────────────────────────────────────────── */}
      <Card title={t.utmGenerator} icon={<Link2 size={15} />} accent="#34D399">
        <p style={{ fontSize: 12, color: '#636366', lineHeight: 1.5, margin: '0 0 12px' }}>{t.utmGenHint}</p>
        <p style={{ fontSize: 11.5, color: '#818CF8', lineHeight: 1.45, margin: '0 0 12px', wordBreak: 'break-all' }}>
          {t.utmBotHint.replace('{name}', `@${shownName}`)}
        </p>

        {/* Быстрые пресеты: готовые связки под канал */}
        <div style={{ marginBottom: 14 }}>
          <div style={labelStyle}>{t.utmPresets}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {PRESETS(t).map(p => (
              <button
                key={p.key}
                onClick={() => applyPreset(p)}
                style={{
                  padding: '7px 12px', borderRadius: 9, cursor: 'pointer',
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#D0D0D5', fontSize: 12, fontWeight: 600,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>{t.utmSource}</label>
          <input list="utm-sources" value={source} onChange={e => setSource(e.target.value)} placeholder={t.utmSourcePlaceholder} style={inputStyle} />
          <datalist id="utm-sources">{SOURCE_OPTS.map(o => <option key={o} value={o} />)}</datalist>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>{t.utmMedium}</label>
          <input list="utm-mediums" value={medium} onChange={e => setMedium(e.target.value)} placeholder={t.utmMediumPlaceholder} style={inputStyle} />
          <datalist id="utm-mediums">{MEDIUM_OPTS.map(o => <option key={o} value={o} />)}</datalist>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>{t.utmCampaign}</label>
          <input list="utm-campaigns" value={campaign} onChange={e => setCampaign(e.target.value)} placeholder={t.utmCampaignPlaceholder} style={inputStyle} />
          <datalist id="utm-campaigns">{CAMPAIGN_OPTS.map(o => <option key={o} value={o} />)}</datalist>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>{t.utmTerm}</label>
          <input list="utm-terms" value={term} onChange={e => setTerm(e.target.value)} placeholder={t.utmTermPlaceholder} style={inputStyle} />
          <datalist id="utm-terms">{TERM_OPTS.map(o => <option key={o} value={o} />)}</datalist>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>{t.utmContent}</label>
          <input list="utm-contents" value={content} onChange={e => setContent(e.target.value)} placeholder={t.utmContentPlaceholder} style={inputStyle} />
          <datalist id="utm-contents">{CONTENT_OPTS.map(o => <option key={o} value={o} />)}</datalist>
        </div>

        <LinkRow label={`${t.utmLinkTelegram} · Telegram`} value={tgLink} copied={copied === 'tg'} onCopy={() => onCopy('tg', tgLink)} onOpen={() => openUrl(tgLink)} t={t} />
        <LinkRow label={`${t.utmLinkWeb} · Web`} value={webLink} copied={copied === 'web'} onCopy={() => onCopy('web', webLink)} onOpen={() => openUrl(webLink)} t={t} />

        <p style={{ fontSize: 11, color: '#636366', lineHeight: 1.45, margin: '4px 0 0' }}>{t.utmGenNote}</p>
      </Card>

      {/* ── Статистика ───────────────────────────────────────────────────── */}
      <Card title={t.utmStats} icon={<MousePointerClick size={15} />} accent="#818CF8">
        {/* Селектор периода */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {DAYS_OPTS.map(d => (
            <button
              key={d}
              onClick={() => { haptic.light(); setDays(d) }}
              style={{
                flex: 1, padding: '9px 0', borderRadius: 10, cursor: 'pointer',
                fontSize: 12.5, fontWeight: 700,
                background: days === d ? 'rgba(129,140,248,0.18)' : 'rgba(255,255,255,0.04)',
                border: days === d ? '1px solid rgba(129,140,248,0.5)' : '1px solid rgba(255,255,255,0.08)',
                color: days === d ? '#A5B4FC' : '#8E8E93',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {t.utmDays.replace('{days}', d)}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '22px 0', color: '#636366', fontSize: 13 }}>
            <Loader2 size={16} color="#34D399" style={{ animation: 'spin 1s linear infinite' }} />
            …
          </div>
        ) : error || !stats ? (
          <div style={{ fontSize: 12.5, color: '#F87171', padding: '14px 2px', lineHeight: 1.5, textAlign: 'center' }}>
            ⚠️ {error || t.utmError}
            <br />
            <button
              onClick={() => { haptic.light(); setError(''); refresh() }}
              style={{
                marginTop: 12, padding: '10px 20px', borderRadius: 10, cursor: 'pointer',
                background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.4)',
                color: '#34D399', fontSize: 13, fontWeight: 600,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {t.tryAgain}
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <div style={{ flex: 1, minWidth: 0, background: 'rgba(0,0,0,0.25)', borderRadius: 12, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: '#636366', marginBottom: 4, lineHeight: 1.3 }}>{t.utmTotal}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#34D399', fontFamily: 'monospace' }}>{stats.total}</div>
              </div>
              <div style={{ flex: 1, minWidth: 0, background: 'rgba(0,0,0,0.25)', borderRadius: 12, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: '#636366', marginBottom: 4, lineHeight: 1.3 }}>{t.utmRevenueTotal}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#F59E0B', fontFamily: 'monospace' }}>{usd(stats.totalRevenue)}</div>
              </div>
            </div>

            {stats.bySource && stats.bySource.length > 0 && (
              <div style={{ fontSize: 11, color: '#636366', margin: '2px 0 6px', letterSpacing: '0.02em' }}>{t.utmBySource}</div>
            )}
            {stats.bySource && stats.bySource.map(r => (
              <Row key={r.source} label={r.source} value={r.users}
                valueSub={`${r.subscribers} ${t.utmSubscribers} · ${conv(r)} · ${t.utmRevenue} ${usd(r.revenue)}`}
                valueColor="#818CF8" />
            ))}

            {stats.byCampaign && stats.byCampaign.length > 0 && (
              <div style={{ fontSize: 11, color: '#636366', margin: '12px 0 6px', letterSpacing: '0.02em' }}>{t.utmByCampaign}</div>
            )}
            {stats.byCampaign && stats.byCampaign.map(r => (
              <Row key={r.campaign} label={r.campaign} value={r.users}
                valueSub={`${r.subscribers} ${t.utmSubscribers} · ${conv(r)} · ${t.utmRevenue} ${usd(r.revenue)}`}
                valueColor="#F59E0B" />
            ))}

            {stats.byEntryMedium && stats.byEntryMedium.length > 0 && (
              <>
                <div style={{ fontSize: 11, color: '#636366', margin: '12px 0 6px', letterSpacing: '0.02em' }}>{t.utmByEntry}</div>
                {stats.byEntryMedium.map(r => (
                  <Row key={r.source} label={r.source} value={r.users} valueColor="#34D399" />
                ))}
              </>
            )}

            {(!stats.bySource || stats.bySource.length === 0) && (
              <div style={{ fontSize: 12.5, color: '#636366', padding: '4px 2px', lineHeight: 1.5 }}>{t.utmRecentEmpty}</div>
            )}

            <div style={{ fontSize: 11, color: '#636366', margin: '12px 0 6px', letterSpacing: '0.02em' }}>{t.utmRecent}</div>
            {stats.recent && stats.recent.length > 0 ? (
              stats.recent.map(r => (
                <Row
                  key={r.user_id}
                  label={`${r.username || r.first_name || ('#' + r.user_id)} · ${r.utm_source || '—'}${r.utm_campaign ? ' / ' + r.utm_campaign : ''}`}
                  value={String(r.first_seen_at || '').slice(0, 16).replace('T', ' ')}
                  valueSub={r.entry_medium || ''}
                  valueColor="#8E8E93"
                  subColor="#34D399"
                />
              ))
            ) : (
              <div style={{ fontSize: 12.5, color: '#636366', padding: '4px 2px', lineHeight: 1.5 }}>{t.utmRecentEmpty}</div>
            )}

            {/* Внутренние аккаунты (владелец + тестовый юзер режима) бэкенд
                исключает из всех UTM-выборок (getUtmStats/getUtmDynamics), чтобы
                заходы самого админа по своим ссылкам не попадали в статистику.
                Пишем это прямо в UI — иначе цифры «не сходятся» с ожиданием. */}
            <p style={{ fontSize: 10.5, color: '#636366', lineHeight: 1.45, margin: '12px 0 0' }}>{t.utmInternalNote}</p>
          </>
        )}
      </Card>

      {/* ── Динамика по дням ─────────────────────────────────────────────── */}
      <Card title={t.utmDynamics} icon={<CalendarDays size={15} />} accent="#F59E0B">
        {!dyn ? (
          <div style={{ fontSize: 12.5, color: '#636366', padding: '4px 2px', lineHeight: 1.5 }}>
            {t.utmDynEmpty}
          </div>
        ) : (
          <>
            <p style={{ fontSize: 11.5, color: '#636366', lineHeight: 1.5, margin: '0 0 12px' }}>
              {t.utmDynHint.replace('{days}', dyn.days)}
            </p>

            {dynRows.length === 0 ? (
              <div style={{ fontSize: 12.5, color: '#636366', padding: '4px 2px', lineHeight: 1.5 }}>
                {t.utmDynEmpty}
              </div>
            ) : dynRows.map(r => (
              <div key={r.day} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
                <div style={{ fontSize: 11, color: '#8E8E93', width: 42, flexShrink: 0, fontFamily: 'monospace' }}>
                  {r.day.slice(8, 10)}.{r.day.slice(5, 7)}
                </div>
                <div style={{ flex: 1, minWidth: 0, height: 16, background: 'rgba(0,0,0,0.25)', borderRadius: 5, overflow: 'hidden', display: 'flex' }}>
                  <div style={{
                    width: `${Math.round(((r.users || 0) / dynMax) * 100)}%`,
                    background: 'linear-gradient(90deg, rgba(129,140,248,0.55), rgba(129,140,248,0.9))',
                  }} />
                </div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: '#818CF8', width: 26, textAlign: 'right', flexShrink: 0, fontFamily: 'monospace' }}>
                  {r.users}
                </div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: r.paid ? '#34D399' : '#3A3A3C', width: 26, textAlign: 'right', flexShrink: 0, fontFamily: 'monospace' }}>
                  {r.paid}
                </div>
              </div>
            ))}

            <div style={{ fontSize: 10.5, color: '#636366', margin: '6px 0 0', lineHeight: 1.45 }}>
              <span style={{ color: '#818CF8', fontWeight: 700 }}>■</span> {t.utmDynUsers}
              {' · '}
              <span style={{ color: '#34D399', fontWeight: 700 }}>■</span> {t.utmDynPaid}
            </div>

            {!!(dyn.byMedium || []).length && (
              <>
                <div style={{ fontSize: 11, color: '#636366', margin: '14px 0 6px', letterSpacing: '0.02em' }}>
                  {t.utmByMedium}
                </div>
                {dyn.byMedium.map(m => (
                  <Row key={m.medium} label={m.medium} value={m.users} valueColor="#F59E0B" />
                ))}
              </>
            )}
          </>
        )}
      </Card>
    </div>
  )
}
